// File Metadata:
// Filename: QualityNotes.js
// Version: 1.00
// Created: 2025-03-23 01:03:13
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState } from 'react'
import {
    Box, TextField, Button, Select,
    MenuItem, FormControl, InputLabel,
    List, ListItem, ListItemText, Divider,
    Typography, Paper, Chip, Stack,
    IconButton, Card, CardHeader,
    CardContent, Collapse
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon, Add as AddIcon } from '@mui/icons-material'
import { useAddQualityNoteMutation, useGetReceptionsQuery } from '../../api/receptionsApiSlice'
import useAuth from '../../../hooks/useAuth'

const CATEGORIES = [
    { id: 'PACKAGING', label: 'Packaging', color: '#2196f3' },
    { id: 'DOCUMENTATION', label: 'Documentation', color: '#4caf50' },
    { id: 'DAMAGE', label: 'Damage', color: '#f44336' },
    { id: 'QUALITY', label: 'Quality', color: '#ff9800' },
    { id: 'OTHER', label: 'Other', color: '#9e9e9e' }
]

const QualityNotes = ({ receptionId }) => {
    const { userId, username } = useAuth()
    const [addQualityNote] = useAddQualityNoteMutation()
    const [note, setNote] = useState('')
    const [category, setCategory] = useState('')
    const [expanded, setExpanded] = useState(false)

    // Get existing notes from reception data
    const { reception } = useGetReceptionsQuery('receptionsList', {
        selectFromResult: ({ data }) => ({
            reception: data?.entities[receptionId]
        })
    })

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!note || !category) return

        try {
            await addQualityNote({
                id: receptionId,
                note: {
                    note,
                    category,
                    addedBy: userId
                }
            })
            setNote('')
            setCategory('')
            setExpanded(false)
        } catch (err) {
            console.error('Failed to add note:', err)
        }
    }

    // Group notes by category
    const groupedNotes = (reception?.qualityNotes || []).reduce((acc, note) => {
        if (!acc[note.category]) {
            acc[note.category] = []
        }
        acc[note.category].push(note)
        return acc
    }, {})

    return (
        <Box sx={{ p: 2 }}>
            {/* Add Note Button & Form */}
            <Card sx={{ mb: 2 }}>
                <CardHeader
                    title="Add Quality Note"
                    action={
                        <IconButton
                            onClick={() => setExpanded(!expanded)}
                            sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                            <ExpandMoreIcon />
                        </IconButton>
                    }
                />
                <Collapse in={expanded}>
                    <CardContent>
                        <form onSubmit={handleSubmit}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={category}
                                    label="Category"
                                    onChange={(e) => setCategory(e.target.value)}
                                >
                                    {CATEGORIES.map(cat => (
                                        <MenuItem key={cat.id} value={cat.id}>
                                            {cat.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Enter quality note..."
                                sx={{ mb: 2 }}
                            />

                            <Button 
                                type="submit"
                                variant="contained"
                                disabled={!note || !category}
                                startIcon={<AddIcon />}
                                fullWidth
                            >
                                Add Note
                            </Button>
                        </form>
                    </CardContent>
                </Collapse>
            </Card>

            {/* Notes List Grouped by Category */}
            {Object.entries(groupedNotes).map(([category, notes]) => {
                const categoryConfig = CATEGORIES.find(c => c.id === category) || CATEGORIES[4]
                return (
                    <Paper key={category} sx={{ mb: 2, p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            <Chip 
                                label={categoryConfig.label}
                                sx={{ 
                                    backgroundColor: categoryConfig.color,
                                    color: 'white',
                                    mr: 1
                                }}
                                size="small"
                            />
                            ({notes.length})
                        </Typography>
                        <List dense>
                            {notes.map((note, index) => (
                                <ListItem key={index} divider={index !== notes.length - 1}>
                                    <ListItemText
                                        primary={note.note}
                                        secondary={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography variant="caption">
                                                    Added by {note.addedBy}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {new Date(note.timestamp).toLocaleString()}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )
            })}
        </Box>
    )
}

export default QualityNotes
